import { useEffect, useState } from "react";

export type Episode = {
  id: string;
  seriesSlug: string;
  season: number;
  number: number;
  title: string;
  url: string;          // direct video / iframe url (will be wrapped by safelink)
  thumbnail?: string;
  duration?: string;
  addedAt: number;
};

const KEY = "rawan-episodes-v1";

type Listener = () => void;
const listeners = new Set<Listener>();

function read(): Episode[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Episode[]) : [];
  } catch {
    return [];
  }
}

function write(next: Episode[]) {
  localStorage.setItem(KEY, JSON.stringify(next));
  listeners.forEach((l) => l());
}

export function getAllEpisodes(): Episode[] {
  return read();
}

export function getEpisodesForSeries(slug: string): Episode[] {
  return read()
    .filter((e) => e.seriesSlug === slug)
    .sort((a, b) => a.season - b.season || a.number - b.number);
}

export function getEpisode(slug: string, season: number, number: number): Episode | undefined {
  return read().find((e) => e.seriesSlug === slug && e.season === season && e.number === number);
}

export function addEpisode(ep: Omit<Episode, "id" | "addedAt">): Episode {
  const next: Episode = { ...ep, id: crypto.randomUUID(), addedAt: Date.now() };
  write([...read(), next]);
  return next;
}

export function deleteEpisode(id: string) {
  write(read().filter((e) => e.id !== id));
}

export function useEpisodes(slug?: string): Episode[] {
  const [, setTick] = useState(0);
  useEffect(() => {
    const l: Listener = () => setTick((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return slug ? getEpisodesForSeries(slug) : getAllEpisodes();
}
